import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Spinner, Offcanvas, OffcanvasHeader, OffcanvasBody, UncontrolledTooltip } from "reactstrap";
import { customersReport } from "../service";
import staticData from "../data/customers_sample.json";

const USE_STATIC_DATA = true;

const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

const getMonthYearString = (date) =>
  date.toLocaleString("default", { month: "short", year: "numeric" });

const getMonthYearDisplay = (date) => {
  return `${date.toLocaleString("default", { month: "short" })} '${date
    .getFullYear()
    .toString()
    .slice(-2)}`;
};

// Get all months from start to end
const getMonthRange = (start, end) => {
  const range = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  startDate.setDate(1);
  while (startDate <= endDate) {
    range.push(new Date(startDate));
    startDate.setMonth(startDate.getMonth() + 1);
  }
  return range;
};

// Get month start date
const getMonthStart = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Get month end date
const getMonthEnd = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null); // 'revenueRetention', 'logoRetention', or 'logoGrowth'
  const [detailSortConfig, setDetailSortConfig] = useState({
    key: null, // null, 'customer', or monthKey
    direction: "asc",
  });

  useEffect(() => {
    setLoading(true);
    if (USE_STATIC_DATA) {
      setData(staticData);
      setError(null);
      setLoading(false);
    } else {
      customersReport()
        .then((response) => {
          const responseData = response?.data || response;
          setData(responseData);
          setError(null);
        })
        .catch((err) => {
          setError(err.message || "Failed to load data");
          console.error("Error fetching customers data:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  // Validate data and transform orders
  const { orders, validationError } = useMemo(() => {
    if (!data) return { orders: [], validationError: null };
    const { organizations = {}, orders: ordersData = [] } = data;

    // Validate all orders first
    for (const order of ordersData) {
      if (!order.placed_date || !order.value) continue;
      const org = organizations[order.organization_id?.toString()];
      if (!org) {
        return {
          orders: [],
          validationError: `Organization not found for order with organization_id: ${order.organization_id}`,
        };
      }

      if (org.demand_periodicity === undefined || org.demand_periodicity === null) {
        return {
          orders: [],
          validationError: `demand_periodicity is missing for organization: ${org.name || order.organization_id}`,
        };
      }
    }

    // If validation passes, transform orders (exclude orders with value < 50000)
    const transformedOrders = ordersData
      .filter((order) => order.placed_date && order.value && order.value >= 50000)
      .map((order) => {
        const org = organizations[order.organization_id?.toString()];
        return {
          organization_id: order.organization_id,
          organization_name: org.name || "Unknown",
          placed_date: order.placed_date,
          value: order.value || 0,
          demand_periodicity: org.demand_periodicity,
        };
      });

    return { orders: transformedOrders, validationError: null };
  }, [data]);

  // Set error state if validation fails
  useEffect(() => {
    if (validationError) {
      setError(validationError);
    } else {
      setError(null);
    }
  }, [validationError]);

  // Get all unique customers with their demand periodicity
  const customers = useMemo(() => {
    const customerMap = new Map();
    orders.forEach((order) => {
      const orgId = order.organization_id;
      if (!customerMap.has(orgId)) {
        customerMap.set(orgId, {
          organization_id: orgId,
          organization_name: order.organization_name,
          demand_periodicity: order.demand_periodicity,
          orders: [],
        });
      }
      customerMap.get(orgId).orders.push({
        placed_date: order.placed_date,
        value: order.value,
      });
    });
    return Array.from(customerMap.values());
  }, [orders]);

  // Calculate metrics for a given reporting month T
  const calculateMetrics = useMemo(() => {
    if (customers.length === 0) return {};

    const today = new Date();
    const endMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startMonth = new Date(today.getFullYear(), today.getMonth() - 11, 1); // Last 12 months

    const months = getMonthRange(startMonth, endMonth);
    const metrics = {};

    months.forEach((reportingMonth) => {
      const metricsForMonth = {
        revenueRetention: null,
        logoRetention: null,
        logoGrowth: null,
      };

      // For each customer, calculate prev_i and curr_i
      const customerMetrics = customers.map((customer) => {
        const f_i = customer.demand_periodicity; // demand periodicity in months
        const T = new Date(reportingMonth);

        // Calculate prev_i: SUM of revenue from months [T - f_i ... T - 1]
        // Start: (T - f_i) month, day 1
        const prevStart = new Date(T.getFullYear(), T.getMonth() - f_i, 1);
        prevStart.setHours(0, 0, 0, 0);

        // End: (T - 1) month, last day
        const prevEnd = new Date(T.getFullYear(), T.getMonth(), 0);
        prevEnd.setHours(23, 59, 59, 999);

        // Calculate curr_i: SUM of revenue from months [T - f_i + 1 ... T]
        // Start: (T - f_i + 1) month, day 1
        const currStart = new Date(T.getFullYear(), T.getMonth() - f_i + 1, 1);
        currStart.setHours(0, 0, 0, 0);

        // End: T month, last day
        const currEnd = new Date(T.getFullYear(), T.getMonth() + 1, 0);
        currEnd.setHours(23, 59, 59, 999);

        let prev_i = 0;
        let curr_i = 0;

        customer.orders.forEach((order) => {
          // Ignore orders with value < 50000
          if (!order.value || order.value < 50000) return;

          const orderDate = new Date(order.placed_date);
          if (orderDate >= prevStart && orderDate <= prevEnd) {
            prev_i += order.value || 0;
          }
          if (orderDate >= currStart && orderDate <= currEnd) {
            curr_i += order.value || 0;
          }
        });

        return {
          organization_id: customer.organization_id,
          organization_name: customer.organization_name,
          prev_i,
          curr_i,
          // Calculate customer-level metrics
          revenueRetention: prev_i > 0 ? curr_i / prev_i : null,
          logoRetention: prev_i > 0 && curr_i > 0 ? 1 : (prev_i > 0 ? 0 : null),
          logoGrowth: prev_i === 0 && curr_i > 0 ? 1 : 0,
        };
      });

      // Filter customers where prev_i > 0 (active in previous window)
      const activeInPrevWindow = customerMetrics.filter((cm) => cm.prev_i > 0);

      if (activeInPrevWindow.length > 0) {
        // Revenue Retention (RR)
        const sumCurrForActive = customerMetrics
          .filter((cm) => cm.prev_i > 0)
          .reduce((sum, cm) => sum + cm.curr_i, 0);
        const sumPrevForActive = activeInPrevWindow.reduce(
          (sum, cm) => sum + cm.prev_i,
          0
        );
        metricsForMonth.revenueRetention =
          sumPrevForActive > 0 ? sumCurrForActive / sumPrevForActive : null;

        // Logo Retention (LR)
        const customersWithPrevAndCurr = customerMetrics.filter(
          (cm) => cm.prev_i > 0 && cm.curr_i > 0
        ).length;
        metricsForMonth.logoRetention =
          activeInPrevWindow.length > 0
            ? customersWithPrevAndCurr / activeInPrevWindow.length
            : null;

        // Logo Growth (LG)
        const newLogos = customerMetrics.filter(
          (cm) => cm.prev_i === 0 && cm.curr_i > 0
        ).length;
        metricsForMonth.logoGrowth =
          activeInPrevWindow.length > 0
            ? newLogos / activeInPrevWindow.length
            : null;
      }

      // Use ISO date string (YYYY-MM) as key for reliable sorting
      const monthKey = `${reportingMonth.getFullYear()}-${String(reportingMonth.getMonth() + 1).padStart(2, '0')}`;
      metrics[monthKey] = {
        ...metricsForMonth,
        displayName: getMonthYearString(reportingMonth),
        date: new Date(reportingMonth),
        customerMetrics: customerMetrics, // Store customer-level data
      };
    });

    return metrics;
  }, [customers]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading data: {error}
      </div>
    );
  }

  // Get all months for table columns, sorted by date
  const allMonths = Object.keys(calculateMetrics).sort((a, b) => {
    return calculateMetrics[a].date - calculateMetrics[b].date;
  });

  const formatMetric = (value) => {
    if (value === null || value === undefined) return "-";
    return (value * 100).toFixed(1) + "%";
  };

  // Get previous month's value for comparison
  const getPreviousMonthValue = (monthKey, metricType) => {
    const allMonthKeys = Object.keys(calculateMetrics).sort((a, b) => {
      return calculateMetrics[a].date - calculateMetrics[b].date;
    });
    const currentIndex = allMonthKeys.indexOf(monthKey);
    if (currentIndex <= 0) return null;
    const prevMonthKey = allMonthKeys[currentIndex - 1];
    return calculateMetrics[prevMonthKey]?.[metricType] ?? null;
  };

  // Get trend indicator (up/down arrow)
  const getTrendIndicator = (currentValue, previousValue) => {
    if (currentValue === null || currentValue === undefined) return null;
    if (previousValue === null || previousValue === undefined) return null;

    if (currentValue > previousValue) {
      return <i className="fa fa-arrow-up text-success ms-1" style={{ fontSize: "0.75rem" }}></i>;
    } else if (currentValue < previousValue) {
      return <i className="fa fa-arrow-down text-danger ms-1" style={{ fontSize: "0.75rem" }}></i>;
    } else {
      return <i className="fa fa-minus text-muted ms-1" style={{ fontSize: "0.75rem" }}></i>;
    }
  };

  // Render sort icon for detail pane
  const renderDetailSortIcon = (key) => {
    if (detailSortConfig.key !== key) return "";
    return detailSortConfig.direction === "asc" ? "▲" : "▼";
  };

  // Handle sort in detail pane
  const handleDetailSort = (key) => {
    if (detailSortConfig.key === key) {
      setDetailSortConfig({
        ...detailSortConfig,
        direction: detailSortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setDetailSortConfig({
        key,
        direction: "asc",
      });
    }
  };

  return (
    <div className="bg-white rounded p-4">
      <h5 className="mb-4 fw-bold">Retention & Growth Metrics</h5>
      <Table bordered responsive size="sm" className="table-striped" style={{ fontSize: "0.9rem" }}>
        <thead>
          <tr>
            <th className="bg-secondary text-white" style={{ minWidth: "200px" }}>Metric</th>
            {allMonths.map((monthKey) => (
              <th key={monthKey} className="bg-secondary text-white text-center" style={{ minWidth: "120px" }}>
                {getMonthYearDisplay(calculateMetrics[monthKey].date)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="fw-semibold">Revenue Retention (RR)</td>
            {allMonths.map((monthKey) => {
              const currentValue = calculateMetrics[monthKey]?.revenueRetention;
              const previousValue = getPreviousMonthValue(monthKey, "revenueRetention");
              return (
                <td
                  key={monthKey}
                  className="text-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedMetric("revenueRetention");
                    setModalOpen(true);
                  }}
                >
                  <span className="d-inline-flex align-items-center justify-content-center">
                    {formatMetric(currentValue)}
                    {getTrendIndicator(currentValue, previousValue)}
                  </span>
                </td>
              );
            })}
          </tr>
          <tr>
            <td className="fw-semibold">Logo Retention (LR)</td>
            {allMonths.map((monthKey) => {
              const currentValue = calculateMetrics[monthKey]?.logoRetention;
              const previousValue = getPreviousMonthValue(monthKey, "logoRetention");
              return (
                <td
                  key={monthKey}
                  className="text-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedMetric("logoRetention");
                    setModalOpen(true);
                  }}
                >
                  <span className="d-inline-flex align-items-center justify-content-center">
                    {formatMetric(currentValue)}
                    {getTrendIndicator(currentValue, previousValue)}
                  </span>
                </td>
              );
            })}
          </tr>
          <tr>
            <td className="fw-semibold">Logo Growth (LG)</td>
            {allMonths.map((monthKey) => {
              const currentValue = calculateMetrics[monthKey]?.logoGrowth;
              const previousValue = getPreviousMonthValue(monthKey, "logoGrowth");
              return (
                <td
                  key={monthKey}
                  className="text-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedMetric("logoGrowth");
                    setModalOpen(true);
                  }}
                >
                  <span className="d-inline-flex align-items-center justify-content-center">
                    {formatMetric(currentValue)}
                    {getTrendIndicator(currentValue, previousValue)}
                  </span>
                </td>
              );
            })}
          </tr>
        </tbody>
      </Table>

      {/* Customer-level details modal */}
      <Offcanvas
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        direction="end"
        style={{ width: "100%" }}
      >
        <OffcanvasHeader toggle={() => setModalOpen(false)}>
          {selectedMetric === "revenueRetention" && "Revenue Retention (RR) - Customer Details"}
          {selectedMetric === "logoRetention" && "Logo Retention (LR) - Customer Details"}
          {selectedMetric === "logoGrowth" && "Logo Growth (LG) - Customer Details"}
        </OffcanvasHeader>
        <OffcanvasBody>
          {selectedMetric && (() => {
            // Get all unique customers across all months
            const customerMap = new Map();
            allMonths.forEach((monthKey) => {
              const customerMetrics = calculateMetrics[monthKey]?.customerMetrics || [];
              customerMetrics.forEach((cm) => {
                if (!customerMap.has(cm.organization_id)) {
                  // Find customer's demand_periodicity from customers array
                  const customer = customers.find(c => c.organization_id === cm.organization_id);
                  customerMap.set(cm.organization_id, {
                    organization_id: cm.organization_id,
                    organization_name: cm.organization_name,
                    demand_periodicity: customer?.demand_periodicity || 2,
                    values: {},
                    prev_i: {}, // Store prev_i for RR tooltip
                    curr_i: {}, // Store curr_i for RR tooltip
                  });
                }
                customerMap.get(cm.organization_id).values[monthKey] = cm[selectedMetric];
                // Store prev_i and curr_i for RR tooltip (always store if they exist, including 0)
                if (cm.prev_i !== undefined && cm.prev_i !== null && cm.curr_i !== undefined && cm.curr_i !== null) {
                  customerMap.get(cm.organization_id).prev_i[monthKey] = cm.prev_i;
                  customerMap.get(cm.organization_id).curr_i[monthKey] = cm.curr_i;
                }
              });
            });

            let customerList = Array.from(customerMap.values());

            // Find onboarded month, track churned months, and find first reactivation month for each customer
            customerList.forEach((customer) => {
              let onboardedMonth = null;
              const churnedMonths = new Set();
              let firstReactivationMonth = null;
              let hasChurned = false;

              for (const monthKey of allMonths) {
                const prev_i = customer.prev_i?.[monthKey];
                const curr_i = customer.curr_i?.[monthKey];

                // Track onboarded month (first month with curr_i > 0 and prev_i === 0)
                if (prev_i === 0 && curr_i !== undefined && curr_i !== null && curr_i > 0 && !onboardedMonth) {
                  onboardedMonth = monthKey;
                }

                // Track churned months (prev_i > 0 and curr_i === 0)
                if (prev_i !== undefined && prev_i !== null && prev_i > 0 &&
                    (curr_i === undefined || curr_i === null || curr_i === 0)) {
                  churnedMonths.add(monthKey);
                  hasChurned = true;
                }

                // Track first reactivation month (first month with curr_i > 0 after churning, regardless of prev_i)
                // Reactivation happens when customer has revenue (curr_i > 0) after having churned
                if (!firstReactivationMonth && hasChurned &&
                    curr_i !== undefined && curr_i !== null && curr_i > 0) {
                  firstReactivationMonth = monthKey;
                }
              }

              customer.onboardedMonth = onboardedMonth;
              customer.churnedMonths = churnedMonths;
              customer.firstReactivationMonth = firstReactivationMonth;
            });

            // Sort customer list
            if (detailSortConfig.key) {
              customerList = [...customerList].sort((a, b) => {
                if (detailSortConfig.key === "customer") {
                  const aVal = (a.organization_name || "").toLowerCase();
                  const bVal = (b.organization_name || "").toLowerCase();
                  if (aVal < bVal) return detailSortConfig.direction === "asc" ? -1 : 1;
                  if (aVal > bVal) return detailSortConfig.direction === "asc" ? 1 : -1;
                  return 0;
                } else {
                  // Sorting by month column
                  const monthKey = detailSortConfig.key;
                  const aPrev_i = a.prev_i?.[monthKey];
                  const aCurr_i = a.curr_i?.[monthKey];
                  const bPrev_i = b.prev_i?.[monthKey];
                  const bCurr_i = b.curr_i?.[monthKey];

                  // For RR metric, sort by: values (sorted) > green (onboarded) > blue (reactivations) > red (churns)
                  if (selectedMetric === "revenueRetention") {
                    // Calculate RR values
                    const aRR = aPrev_i > 0 ? (aCurr_i || 0) / aPrev_i : null;
                    const bRR = bPrev_i > 0 ? (bCurr_i || 0) / bPrev_i : null;

                    // Check if onboarded month (green)
                    const aIsOnboarded = a.onboardedMonth === monthKey;
                    const bIsOnboarded = b.onboardedMonth === monthKey;

                    // Check if reactivation month (blue)
                    const aIsReactivation = a.firstReactivationMonth === monthKey;
                    const bIsReactivation = b.firstReactivationMonth === monthKey;

                    // Check if churn (red) - prev_i > 0 and curr_i === 0
                    const aIsChurn = aPrev_i > 0 && (aCurr_i === undefined || aCurr_i === null || aCurr_i === 0);
                    const bIsChurn = bPrev_i > 0 && (bCurr_i === undefined || bCurr_i === null || bCurr_i === 0);

                    // Check if blank (prev_i === 0 or undefined/null)
                    const aIsBlank = aPrev_i === undefined || aPrev_i === null || aPrev_i === 0;
                    const bIsBlank = bPrev_i === undefined || bPrev_i === null || bPrev_i === 0;

                    // Priority order: values (sorted) > green (onboarded) > blue (reactivations) > red (churns) > blanks

                    // Values: positive RR (not onboarded, not reactivation, not churn, not blank, RR > 0)
                    const aIsValue = !aIsOnboarded && !aIsReactivation && !aIsChurn && !aIsBlank && aRR !== null && aRR > 0;
                    const bIsValue = !bIsOnboarded && !bIsReactivation && !bIsChurn && !bIsBlank && bRR !== null && bRR > 0;

                    if (aIsValue && bIsValue) {
                      // Both are values, sort by RR value
                      if (aRR < bRR) return detailSortConfig.direction === "asc" ? -1 : 1;
                      if (aRR > bRR) return detailSortConfig.direction === "asc" ? 1 : -1;
                      return 0;
                    }
                    if (aIsValue && !bIsValue) return -1; // a comes first
                    if (!aIsValue && bIsValue) return 1; // b comes first

                    // Green (onboarded)
                    if (aIsOnboarded && bIsOnboarded) return 0;
                    if (aIsOnboarded && !bIsOnboarded && !bIsValue) return -1;
                    if (!aIsOnboarded && !aIsValue && bIsOnboarded) return 1;

                    // Blue (reactivations)
                    if (aIsReactivation && bIsReactivation) return 0;
                    if (aIsReactivation && !bIsReactivation && !bIsValue && !bIsOnboarded) return -1;
                    if (!aIsReactivation && !aIsValue && !aIsOnboarded && bIsReactivation) return 1;

                    // Red (churns)
                    if (aIsChurn && bIsChurn) return 0;
                    if (aIsChurn && !bIsChurn && !bIsValue && !bIsOnboarded && !bIsReactivation) return -1;
                    if (!aIsChurn && !aIsValue && !aIsOnboarded && !aIsReactivation && bIsChurn) return 1;

                    // Blanks (everything else)
                    return 0;
                  } else {
                    // For other metrics (LR, LG), use standard sorting
                    let aVal = a.values[monthKey];
                    let bVal = b.values[monthKey];

                    // Handle null/undefined values
                    if (aVal === null || aVal === undefined) aVal = -Infinity;
                    if (bVal === null || bVal === undefined) bVal = -Infinity;

                    if (aVal < bVal) return detailSortConfig.direction === "asc" ? -1 : 1;
                    if (aVal > bVal) return detailSortConfig.direction === "asc" ? 1 : -1;
                    return 0;
                  }
                }
              });
            }

            // Calculate monthly totals - sum of all order values placed in each month
            const monthlyTotals = {};
            allMonths.forEach((monthKey) => {
              // Get the month date from calculateMetrics
              const monthDate = calculateMetrics[monthKey]?.date;
              if (!monthDate) {
                monthlyTotals[monthKey] = 0;
                return;
              }

              // Calculate start and end of the month
              const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
              monthStart.setHours(0, 0, 0, 0);
              const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
              monthEnd.setHours(23, 59, 59, 999);

              // Sum all order values placed in this month (value >= 50000)
              let total = 0;
              orders.forEach((order) => {
                if (!order.placed_date || !order.value || order.value < 50000) return;
                const orderDate = new Date(order.placed_date);
                if (orderDate >= monthStart && orderDate <= monthEnd) {
                  total += order.value || 0;
                }
              });

              monthlyTotals[monthKey] = total;
            });

            return (
              <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto", overflowX: "auto" }}>
                <style>{`
                  .dashboard-detail-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 1020;
                    background-color: #6c757d !important;
                  }
                  .dashboard-detail-table thead tr:nth-child(2) {
                    position: relative;
                  }
                  .dashboard-detail-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 31px;
                    z-index: 1019;
                    background-color: #5a6268 !important;
                    box-sizing: border-box;
                  }
                `}</style>
                <Table bordered size="sm" className="table-striped dashboard-detail-table" style={{ fontSize: "0.9rem", marginBottom: 0 }}>
                  <thead className="bg-secondary">
                    <tr>
                      <th
                        className="bg-secondary text-white pointer"
                        style={{ minWidth: "200px" }}
                        onClick={() => handleDetailSort("customer")}
                      >
                        Customer {renderDetailSortIcon("customer")}
                      </th>
                      {allMonths.map((monthKey) => (
                        <th
                          key={monthKey}
                          className="bg-secondary text-white text-center pointer"
                          style={{ minWidth: "120px" }}
                          onClick={() => handleDetailSort(monthKey)}
                        >
                          {getMonthYearDisplay(calculateMetrics[monthKey].date)} {renderDetailSortIcon(monthKey)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th className="bg-secondary text-white" style={{ minWidth: "200px", backgroundColor: "#5a6268 !important" }}>
                        Total
                      </th>
                      {allMonths.map((monthKey) => (
                        <th
                          key={monthKey}
                          className="bg-secondary text-white text-center"
                          style={{ minWidth: "120px", backgroundColor: "#5a6268 !important" }}
                        >
                          <span style={{ fontSize: "0.85rem" }}>
                            ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(monthlyTotals[monthKey] || 0)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                <tbody>
                  {customerList.map((customer) => (
                    <tr key={customer.organization_id}>
                      <td className="fw-semibold">
                        {customer.organization_name}
                        {customer.demand_periodicity && (
                          <span className="text-muted small ms-2" style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
                            ({customer.demand_periodicity}m)
                          </span>
                        )}
                      </td>
                      {allMonths.map((monthKey) => {
                        const value = customer.values[monthKey];
                        const prev_i = customer.prev_i?.[monthKey];
                        const curr_i = customer.curr_i?.[monthKey];

                        // For RR, only show cells where prev_i > 0 (customer was active in previous window)
                        if (selectedMetric === "revenueRetention") {
                          // Check if this is the onboarded month (curr_i > 0 and prev_i === 0)
                          const isOnboardedMonth = customer.onboardedMonth === monthKey;
                          // Check if this is the reactivation month
                          const isReactivationMonth = customer.firstReactivationMonth === monthKey;

                          if (isOnboardedMonth) {
                            const tooltipId = `rr-tooltip-${customer.organization_id}-${monthKey}`;
                            const curr_i = customer.curr_i?.[monthKey] || 0;
                            return (
                              <td key={monthKey} className="text-center">
                                <span
                                  id={tooltipId}
                                  style={{
                                    cursor: "help",
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: "#28a745",
                                  }}
                                ></span>
                                <UncontrolledTooltip target={tooltipId} placement="top">
                                  <div style={{ textAlign: "left" }}>
                                    <div><strong>Onboarded</strong></div>
                                    <div><strong>Current window (${customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i)}</div>
                                  </div>
                                </UncontrolledTooltip>
                              </td>
                            );
                          }

                          // Show reactivation month even if prev_i = 0
                          if (isReactivationMonth) {
                            const tooltipId = `rr-tooltip-${customer.organization_id}-${monthKey}`;
                            const rrValue = prev_i > 0 ? (curr_i || 0) / prev_i : null;

                            return (
                              <td key={monthKey} className="text-center">
                                <span
                                  id={tooltipId}
                                  style={{
                                    cursor: "help",
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: "#007bff",
                                  }}
                                ></span>
                                <UncontrolledTooltip target={tooltipId} placement="top">
                                  <div style={{ textAlign: "left" }}>
                                    <div><strong>Reactivation</strong></div>
                                    <div style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                                      Customer had churned in a previous month and has now reactivated.
                                    </div>
                                    <div style={{ marginTop: "8px" }}>
                                      <div><strong>Current {customer.demand_periodicity} months</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      {prev_i > 0 && (
                                        <>
                                          <div><strong>Last {customer.demand_periodicity} months</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                          <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                            RR = {(rrValue * 100).toFixed(1)}%
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </UncontrolledTooltip>
                              </td>
                            );
                          }

                          // Only show cells where prev_i > 0 (customer was active in previous window)
                          if (prev_i === undefined || prev_i === null || prev_i === 0) {
                            return <td key={monthKey} className="text-center"></td>;
                          }

                          // Calculate RR value
                          const rrValue = prev_i > 0 ? (curr_i || 0) / prev_i : 0;
                          const tooltipId = `rr-tooltip-${customer.organization_id}-${monthKey}`;

                          return (
                            <td key={monthKey} className="text-center">
                              {rrValue === 0 ? (
                                <>
                                  <span
                                    id={tooltipId}
                                    style={{
                                      cursor: "help",
                                      display: "inline-block",
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "50%",
                                      backgroundColor: "#dc3545",
                                    }}
                                  ></span>
                                  <UncontrolledTooltip target={tooltipId} placement="top">
                                    <div style={{ textAlign: "left" }}>
                                      <div><strong>Churn</strong></div>
                                      <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      <div><strong>Previous window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                      <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                        RR = 0%
                                      </div>
                                    </div>
                                  </UncontrolledTooltip>
                                </>
                              ) : (
                                <>
                                  <span
                                    id={tooltipId}
                                    style={{
                                      cursor: "help",
                                    }}
                                  >
                                    {(rrValue * 100).toFixed(1) + "%"}
                                  </span>
                                  <UncontrolledTooltip target={tooltipId} placement="top">
                                    <div style={{ textAlign: "left" }}>
                                      <div><strong>Current window (${customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      <div><strong>Previous window (${customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                      <div style={{ fontSize: "0.75rem", marginTop: "4px", color: "#666" }}>
                                        Note: Windows overlap by 1 month
                                      </div>
                                      <div style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                                        {prev_i > 0 ? `RR = ${(rrValue * 100).toFixed(1)}%` : "N/A"}
                                      </div>
                                    </div>
                                  </UncontrolledTooltip>
                                </>
                              )}
                            </td>
                          );
                        }

                        // For LR and LG metrics - show circles like RR
                        if (selectedMetric === "logoRetention" || selectedMetric === "logoGrowth") {
                          const isOnboardedMonth = customer.onboardedMonth === monthKey;
                          const isReactivationMonth = customer.firstReactivationMonth === monthKey;

                          // For LR: Green = retained (prev_i > 0 && curr_i > 0), Red = churned (prev_i > 0 && curr_i === 0)
                          // For LG: Green = new logo (prev_i === 0 && curr_i > 0)

                          if (selectedMetric === "logoRetention") {
                            // LR: Show green if retained, red if churned, blue if reactivation
                            if (isOnboardedMonth) {
                              // Onboarded customers don't count for LR (they're new, not retained)
                              return <td key={monthKey} className="text-center"></td>;
                            }

                            if (isReactivationMonth) {
                              const tooltipId = `lr-tooltip-${customer.organization_id}-${monthKey}`;
                              return (
                                <td key={monthKey} className="text-center">
                                  <span
                                    id={tooltipId}
                                    style={{
                                      cursor: "help",
                                      display: "inline-block",
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "50%",
                                      backgroundColor: "#007bff",
                                    }}
                                  ></span>
                                  <UncontrolledTooltip target={tooltipId} placement="top">
                                    <div style={{ textAlign: "left" }}>
                                      <div><strong>Reactivation</strong></div>
                                      <div style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                                        Customer had churned and has now reactivated.
                                      </div>
                                      <div style={{ marginTop: "8px" }}>
                                        <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                        {prev_i > 0 && (
                                          <div><strong>Previous window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                        )}
                                      </div>
                                    </div>
                                  </UncontrolledTooltip>
                                </td>
                              );
                            }

                            // Only show cells where prev_i > 0 (customer was active in previous window)
                            if (prev_i === undefined || prev_i === null || prev_i === 0) {
                              return <td key={monthKey} className="text-center"></td>;
                            }

                            const tooltipId = `lr-tooltip-${customer.organization_id}-${monthKey}`;
                            const isRetained = curr_i > 0;

                            return (
                              <td key={monthKey} className="text-center">
                                <span
                                  id={tooltipId}
                                  style={{
                                    cursor: "help",
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: isRetained ? "#28a745" : "#dc3545",
                                  }}
                                ></span>
                                <UncontrolledTooltip target={tooltipId} placement="top">
                                  <div style={{ textAlign: "left" }}>
                                    <div><strong>{isRetained ? "Retained" : "Churned"}</strong></div>
                                    <div style={{ marginTop: "8px" }}>
                                      <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      <div><strong>Previous window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(prev_i)}</div>
                                    </div>
                                  </div>
                                </UncontrolledTooltip>
                              </td>
                            );
                          }

                          if (selectedMetric === "logoGrowth") {
                            // LG: Green = new logo (prev_i === 0 && curr_i > 0), Blue = reactivation
                            if (isOnboardedMonth) {
                              const tooltipId = `lg-tooltip-${customer.organization_id}-${monthKey}`;
                              return (
                                <td key={monthKey} className="text-center">
                                  <span
                                    id={tooltipId}
                                    style={{
                                      cursor: "help",
                                      display: "inline-block",
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "50%",
                                      backgroundColor: "#28a745",
                                    }}
                                  ></span>
                                  <UncontrolledTooltip target={tooltipId} placement="top">
                                    <div style={{ textAlign: "left" }}>
                                      <div><strong>New Logo</strong></div>
                                      <div style={{ marginTop: "8px" }}>
                                        <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      </div>
                                    </div>
                                  </UncontrolledTooltip>
                                </td>
                              );
                            }

                            if (isReactivationMonth) {
                              const tooltipId = `lg-tooltip-${customer.organization_id}-${monthKey}`;
                              return (
                                <td key={monthKey} className="text-center">
                                  <span
                                    id={tooltipId}
                                    style={{
                                      cursor: "help",
                                      display: "inline-block",
                                      width: "12px",
                                      height: "12px",
                                      borderRadius: "50%",
                                      backgroundColor: "#007bff",
                                    }}
                                  ></span>
                                  <UncontrolledTooltip target={tooltipId} placement="top">
                                    <div style={{ textAlign: "left" }}>
                                      <div><strong>Reactivation</strong></div>
                                      <div style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                                        Customer had churned and has now reactivated.
                                      </div>
                                      <div style={{ marginTop: "8px" }}>
                                        <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                      </div>
                                    </div>
                                  </UncontrolledTooltip>
                                </td>
                              );
                            }

                            // For LG, only show cells where prev_i === 0 and curr_i > 0 (new logos)
                            if (prev_i === undefined || prev_i === null || prev_i === 0) {
                              if (curr_i > 0) {
                                // This is a new logo
                                const tooltipId = `lg-tooltip-${customer.organization_id}-${monthKey}`;
                                return (
                                  <td key={monthKey} className="text-center">
                                    <span
                                      id={tooltipId}
                                      style={{
                                        cursor: "help",
                                        display: "inline-block",
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "50%",
                                        backgroundColor: "#28a745",
                                      }}
                                    ></span>
                                    <UncontrolledTooltip target={tooltipId} placement="top">
                                      <div style={{ textAlign: "left" }}>
                                        <div><strong>New Logo</strong></div>
                                        <div style={{ marginTop: "8px" }}>
                                          <div><strong>Current window ({customer.demand_periodicity}m):</strong> ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(curr_i || 0)}</div>
                                        </div>
                                      </div>
                                    </UncontrolledTooltip>
                                  </td>
                                );
                              }
                              return <td key={monthKey} className="text-center"></td>;
                            }

                            // Not a new logo (prev_i > 0), so blank
                            return <td key={monthKey} className="text-center"></td>;
                          }
                        }

                        // Fallback (shouldn't reach here)
                        return (
                          <td key={monthKey} className="text-center">
                            -
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            );
          })()}
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default Dashboard;

